import { type FormEvent, useEffect, useState } from 'react'
import {
  ArrowRight,
  Award,
  Building2,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Gavel,
  Heart,
  Landmark,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  Scale,
  Send,
  ShieldCheck,
} from 'lucide-react'
import PrivacyPolicy from './PrivacyPolicy'
import TurnstileWidget from './TurnstileWidget'
import { AttorneyPage, PracticeAreaPage, type PracticeSlug } from './DetailPages'
import SiteFooter from './SiteFooter'
import SiteHeader from './SiteHeader'

const phoneDisplay = '214-754-5500'
const phoneHref = 'tel:+12147545500'
const barProfile =
  'https://www.texasbar.com/am/template.cfm?Template=/Customsource/MemberDirectory/MemberDirectoryDetail.cfm&contactid=161789'
const barEmail =
  'https://www.texasbar.com/AM/Template.cfm?Section=Home&template=/MemberDirectory/MemberDirectoryEmail.cfm&ContactID=161789'
const mapUrl =
  'https://www.google.com/maps/search/?api=1&query=5220+Spring+Valley+Rd+Ste+618+Dallas+TX+75254'

const practices = [
  {
    slug: 'commercial-litigation' as PracticeSlug,
    icon: Gavel,
    title: 'Commercial Litigation',
    description:
      'Counsel for business disputes and commercial matters, with a focus on practical strategy and clear communication.',
  },
  {
    slug: 'personal-injury-litigation' as PracticeSlug,
    icon: ShieldCheck,
    title: 'Personal Injury Litigation',
    description:
      'Representation in personal injury litigation, bringing disciplined preparation to every stage of the matter.',
  },
  {
    slug: 'real-estate' as PracticeSlug,
    icon: Building2,
    title: 'Real Estate',
    description:
      'Guidance in real estate matters for individuals and businesses navigating disputes and legal decisions.',
  },
]

const admissions = [
  'U.S. Supreme Court',
  'Fifth Circuit Court of Appeals',
  'Tenth Circuit Court of Appeals',
  'U.S. District Courts in Texas',
  'U.S. District Courts in Oklahoma',
]

function Website() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [formMessage, setFormMessage] = useState('')
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const [captchaResetKey, setCaptchaResetKey] = useState(0)

  useEffect(() => {
    if (window.location.hash !== '#contact') return

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('contact')?.scrollIntoView({ block: 'start' })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    fetch('/api/config', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Configuration unavailable.')
        return response.json() as Promise<{ turnstileSiteKey?: string }>
      })
      .then(({ turnstileSiteKey: siteKey }) => {
        if (siteKey) {
          setTurnstileSiteKey(siteKey)
        } else {
          setCaptchaError('Human verification is temporarily unavailable. Please call the office instead.')
        }
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return
        setCaptchaError('Human verification is temporarily unavailable. Please call the office instead.')
      })

    return () => controller.abort()
  }, [])

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!captchaToken) {
      setCaptchaError('Please complete the human verification before sending your message.')
      return
    }

    const form = event.currentTarget
    const formData = new FormData(form)
    const payload = {
      ...Object.fromEntries(formData.entries()),
      'cf-turnstile-response': captchaToken,
    }

    setFormStatus('submitting')
    setFormMessage('')
    setCaptchaError('')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = (await response.json()) as { message?: string }

      if (!response.ok) {
        throw new Error(result.message || 'Your message could not be sent. Please call the office instead.')
      }

      form.reset()
      setFormStatus('success')
      setFormMessage(result.message || 'Thank you. Your message has been sent to the office.')
    } catch (error) {
      setFormStatus('error')
      setFormMessage(
        error instanceof Error
          ? error.message
          : 'Your message could not be sent. Please call the office instead.',
      )
    } finally {
      setCaptchaToken('')
      setCaptchaResetKey((key) => key + 1)
    }
  }

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <SiteHeader />

      <main id="main-content">
        <section className="hero" id="home">
          <div className="hero-grid" aria-hidden="true" />
          <div className="container hero-inner">
            <div className="hero-copy reveal">
              <p className="eyebrow">Dallas trial and real estate counsel</p>
              <h1>
                Experience that brings
                <span> clarity to complex matters.</span>
              </h1>
              <p className="hero-lede">
                The Law Office of Robert W. Buchholz, P.C. represents clients in
                commercial litigation, personal injury litigation, and real estate matters.
              </p>
              <div className="hero-actions">
                <a className="button button-gold" href={phoneHref}>
                  <Phone size={18} aria-hidden="true" />
                  Call {phoneDisplay}
                </a>
                <a className="text-link" href="#practice-areas">
                  Explore practice areas <ArrowRight size={18} aria-hidden="true" />
                </a>
              </div>
              <div className="hero-trust" aria-label="Professional credentials">
                <div>
                  <Award aria-hidden="true" />
                  <span><strong>Board Certified</strong>Civil Trial Law</span>
                </div>
                <div>
                  <Landmark aria-hidden="true" />
                  <span><strong>Licensed since 1979</strong>State Bar of Texas</span>
                </div>
              </div>
            </div>

            <div className="hero-art" aria-hidden="true">
              <div className="monogram">RB</div>
              <div className="art-caption">
                <span>Robert W. Buchholz</span>
                <small>Attorney at Law</small>
              </div>
            </div>
          </div>
          <div className="hero-edge" aria-hidden="true" />
        </section>

        <section className="section practice-section" id="practice-areas">
          <div className="container">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow dark">How the firm can help</p>
                <h2>Focused legal representation.</h2>
              </div>
              <p>
                A focused solo practice means direct attorney involvement and a
                deliberate approach tailored to the matter at hand.
              </p>
            </div>

            <div className="practice-grid">
              {practices.map(({ slug, icon: Icon, title, description }, index) => (
                <article className="practice-card" key={title}>
                  <div className="card-number">0{index + 1}</div>
                  <Icon className="practice-icon" aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <a href={`/${slug}`}>
                    Learn about {title} <ChevronRight size={17} aria-hidden="true" />
                  </a>
                </article>
              ))}
            </div>
            <p className="section-note">
              Every legal matter is different. A consultation is needed to determine
              whether the firm can assist with a particular situation.
            </p>
          </div>
        </section>

        <section className="section attorney-section" id="attorney">
          <div className="container attorney-layout">
            <div className="attorney-visual">
              <div className="portrait-placeholder" role="img" aria-label="Robert W. Buchholz monogram portrait placeholder">
                <span>RWB</span>
              </div>
              <div className="experience-badge">
                <strong>1979</strong>
                <span>Texas license date</span>
              </div>
            </div>

            <div className="attorney-copy">
              <p className="eyebrow dark">Meet your attorney</p>
              <h2>Robert W. “Bob” Buchholz</h2>
              <p className="role">Attorney &amp; Founder</p>
              <p className="lead-copy">
                Bob Buchholz is a Dallas attorney whose practice is concentrated in
                commercial litigation, personal injury litigation, and real estate.
              </p>
              <p>
                Licensed in Texas since 1979, he is Board Certified in Civil Trial Law.
                He earned his law degree from Southern Methodist University and is also
                licensed in Arkansas and Oklahoma.
              </p>

              <div className="personal-bio">
                <Heart aria-hidden="true" />
                <div>
                  <h3>Beyond the office</h3>
                  <p>
                    Outside his legal practice, Bob is a devoted husband and proud
                    father of three sons. His family is an important part of his life.
                  </p>
                </div>
              </div>

              <div className="bio-facts">
                <div>
                  <Scale aria-hidden="true" />
                  <span><strong>Board Certification</strong>Civil Trial Law</span>
                </div>
                <div>
                  <Landmark aria-hidden="true" />
                  <span><strong>Law School</strong>Southern Methodist University, 1979</span>
                </div>
              </div>

              <div className="attorney-links">
                <a className="text-link dark-link" href="/attorney">
                  View attorney profile <ChevronRight size={16} aria-hidden="true" />
                </a>
                <a className="text-link dark-link" href={barProfile} target="_blank" rel="noreferrer">
                  Verify State Bar profile <ExternalLink size={16} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="section credentials-section" id="credentials">
          <div className="container credentials-layout">
            <div className="credentials-copy">
              <p className="eyebrow">Professional credentials</p>
              <h2>Admitted across state and federal courts.</h2>
              <p>
                Robert W. Buchholz is licensed in Texas, Arkansas, and Oklahoma and
                reports admission to the following federal courts.
              </p>
              <a className="button button-outline" href={barProfile} target="_blank" rel="noreferrer">
                View official profile <ExternalLink size={16} aria-hidden="true" />
              </a>
            </div>
            <div className="admissions-list">
              {admissions.map((admission) => (
                <div key={admission}>
                  <CheckCircle2 aria-hidden="true" />
                  <span>{admission}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section contact-section" id="contact">
          <div className="container contact-layout">
            <div className="contact-heading">
              <p className="eyebrow dark">Get in touch</p>
              <h2>Start a conversation about your legal matter.</h2>
              <p>
                Contact the office to discuss whether the firm may be able to assist.
                Calling the office does not create an attorney-client relationship.
              </p>
              <div className="contact-details">
                <a href={phoneHref}>
                  <span className="contact-icon"><Phone aria-hidden="true" /></span>
                  <span><small>Call the office</small><strong>{phoneDisplay}</strong></span>
                </a>
                <a href={mapUrl} target="_blank" rel="noreferrer">
                  <span className="contact-icon"><MapPin aria-hidden="true" /></span>
                  <span>
                    <small>Dallas office</small>
                    <strong>5220 Spring Valley Rd, Suite 618<br />Dallas, TX 75254</strong>
                  </span>
                </a>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleContactSubmit} method="post" action="/api/contact">
              <div className="form-heading">
                <span className="form-icon"><Mail aria-hidden="true" /></span>
                <div>
                  <h3>Send the office a message</h3>
                  <p>Fields marked with an asterisk are required.</p>
                </div>
              </div>

              <div className="confidentiality-notice" id="confidentiality-notice" role="note">
                <ShieldCheck aria-hidden="true" />
                <p>
                  <strong>Protect your information.</strong> Do not submit privileged,
                  confidential, medical, or time-sensitive information through this form.
                  If your matter is urgent, call the office at <a href={phoneHref}>{phoneDisplay}</a>.
                </p>
              </div>

              <div className="honeypot" aria-hidden="true">
                <label htmlFor="company">Company website</label>
                <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="name">Full name <span aria-hidden="true">*</span></label>
                  <input id="name" name="name" type="text" autoComplete="name" maxLength={100} required />
                </div>
                <div className="form-field">
                  <label htmlFor="email">Email address <span aria-hidden="true">*</span></label>
                  <input id="email" name="email" type="email" autoComplete="email" maxLength={254} required />
                </div>
                <div className="form-field">
                  <label htmlFor="phone">Phone number</label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" maxLength={30} />
                </div>
                <div className="form-field">
                  <label htmlFor="matter">Type of matter <span aria-hidden="true">*</span></label>
                  <select id="matter" name="matter" defaultValue="" required>
                    <option value="" disabled>Select one</option>
                    <option value="Commercial litigation">Commercial litigation</option>
                    <option value="Personal injury litigation">Personal injury litigation</option>
                    <option value="Real estate">Real estate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-field form-field-full">
                  <label htmlFor="message">Brief description <span aria-hidden="true">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    maxLength={3000}
                    aria-describedby="confidentiality-notice message-help"
                    required
                  />
                  <small id="message-help">Provide only a general, non-confidential description of your inquiry.</small>
                </div>
              </div>

              <label className="consent-field">
                <input name="consent" type="checkbox" value="accepted" required />
                <span>
                  I understand that submitting this form does not create an attorney-client
                  relationship, and I have not included privileged, confidential, medical,
                  or time-sensitive information. I have read the <a href="/privacy" target="_blank">Privacy Policy</a>.
                  <span aria-hidden="true"> *</span>
                </span>
              </label>

              <div className="captcha-field">
                <span className="captcha-label">Human verification <span aria-hidden="true">*</span></span>
                {turnstileSiteKey ? (
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    resetKey={captchaResetKey}
                    onTokenChange={(token) => {
                      setCaptchaToken(token)
                      if (token) setCaptchaError('')
                    }}
                    onError={() => setCaptchaError('Verification could not be completed. Please try again or call the office.')}
                  />
                ) : (
                  <div className="captcha-placeholder" aria-hidden="true">Loading verification…</div>
                )}
                {captchaError && <p className="captcha-error" role="alert">{captchaError}</p>}
                <small>This site uses Cloudflare Turnstile to prevent automated spam.</small>
              </div>

              <div className="form-actions">
                <button className="button button-submit" type="submit" disabled={formStatus === 'submitting' || !captchaToken}>
                  {formStatus === 'submitting' ? 'Sending…' : 'Send message'}
                  <Send size={17} aria-hidden="true" />
                </button>
                <span className="privacy-note"><LockKeyhole size={14} aria-hidden="true" />Protected in transit with HTTPS.</span>
              </div>

              {formMessage && (
                <div
                  className={`form-alert ${formStatus === 'success' ? 'is-success' : 'is-error'}`}
                  role={formStatus === 'error' ? 'alert' : 'status'}
                  aria-live="polite"
                >
                  {formMessage}
                  {formStatus === 'error' && <a href={phoneHref}>Call {phoneDisplay}</a>}
                </div>
              )}

              <p className="alternate-contact">
                Prefer another option?{' '}
                <a href={barEmail} target="_blank" rel="noreferrer">
                  Use the State Bar contact service <ExternalLink size={13} aria-hidden="true" />
                </a>
              </p>
            </form>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}

function App() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/privacy') return <PrivacyPolicy />
  if (path === '/attorney') return <AttorneyPage />
  if (path === '/commercial-litigation') return <PracticeAreaPage slug="commercial-litigation" />
  if (path === '/personal-injury-litigation') return <PracticeAreaPage slug="personal-injury-litigation" />
  if (path === '/real-estate') return <PracticeAreaPage slug="real-estate" />
  return <Website />
}

export default App
